import React from 'react';
import LinkPrimitive from 'next/link';

import { cn, isEmpty } from '@/lib/utils';
import type { UrlObject } from 'url';
import { Button, ButtonBaseProps, buttonVariants } from '../button';
import { VariantProps } from 'class-variance-authority';

export type LinkProps = React.ComponentPropsWithoutRef<typeof LinkPrimitive> &
  VariantProps<typeof buttonVariants> & {
    'data-ta'?: string;
    disabled?: ButtonBaseProps['disabled'];
  };

/**
 * The `Link` component is a wrapper around the Next.js `Link` component that provides additional GDS design token styling and functionality.
 * It supports internal and external links, different variants, sizes, and colors.
 * This `Link` component should not be used inside a Button component, rely on the Next.js `Link` primitive component with Button prop `asChild` instead.
 *
 * @example
 * ```tsx
 * import { Link } from 'components/ui/link';
 * // Internal link that opens in the same tab
 * <Link href="/test">Test Link</Link>
 *
 * // Internal link with a variant and link color
 * <Link href="/test" mode="standalone" textColor="white">Test Link</Link>
 *
 * // External link with a custom data-ta for easier testing identification
 * <Link href="https://mdn.io" data-ta="custom-link">External Link</Link>
 * ```
 */
const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(
  (
    { className, href, variant = 'link', disabled, children, ...props },
    ref
  ) => {
    if (
      isEmpty(href) ||
      // Next.js Link component can use UrlObject for href
      (typeof href === 'object' &&
        'pathname' in href &&
        (typeof href.pathname !== 'string' || isEmpty(href.pathname)))
    ) {
      const typeOfHref = isEmpty(href)
        ? 'undefined or empty string'
        : typeof href;
      return (
        <p
          className={cn('text-error', className)}
          role="alert"
          data-ta={props['data-ta']}
        >
          Broken link: href cannot be {typeOfHref} (&quot;{children}&quot;)
        </p>
      );
    }

    return (
      <Button
        // important to use asChild here to avoid nesting button with a link
        asChild
        variant={variant}
        className={className}
        disabled={!!disabled}
        data-ta={props['data-ta']}
      >
        <LinkPrimitive
          href={href}
          {...(isExternal(href) && {
            target: '_blank',
            rel: 'noopener noreferrer',
          })}
          {...(disabled && { 'aria-disabled': true, tabIndex: -1 })}
          ref={ref}
          {...props}
        >
          {children}
          {disabled && !isExternal(href) ? null : (
            <span className="hidden"> opens in a new tab</span>
          )}
        </LinkPrimitive>
      </Button>
    );
  }
);

const isExternal = (href: string | UrlObject): boolean => {
  if (typeof href === 'string') {
    return href.startsWith('http');
  } else if (typeof href === 'object' && href !== null) {
    return typeof href.href === 'string' && href.href.startsWith('http');
  }
  return false;
};

Link.displayName = 'Link';

export { Link, isExternal };
